import * as React from 'react';

import { useChatPanelCommonProps } from '@/features/ChatBotV2/components/ChatPanel/useChatPanelCommonProps';
import { Footer } from '@/features/ChatBotV2/components/Footer';
import type { AskNeedlReportType } from '@/features/ChatBotV2/context';
import { useAskNeedlContext } from '@/features/ChatBotV2/context';
import type { SessionState } from '@/features/ChatBotV2/types';
import { useConfigUI } from '@/hooks/query/useFeatureConfig';
import { useResponsiveConstants } from '@/hooks/responsive/useResponsiveConstants';
import { useLocationMatch } from '@/hooks/useLocationMatch';
import { getLogo } from '@/utils/getIcons';

import { DefaultQuestions } from './DefaultQuestions';
import { NeedlReportPlaceholder } from './NeedlReportPlaceholder';
import { NeedlReportQuestions } from './NeedlReportQuestions';

export const Placeholder = ({
  handleAskQuestion,
  pro,
  sessionState,
  report,
  lastActiveSessionId,
}: PlaceholderType) => {
  const {
    data: { searchWithin, currentFeedOrDocumentId },
  } = useAskNeedlContext();
  const { isLoading: isFeatureConfigLoading, data: featureConfig } =
    useConfigUI();

  const { askNeedlPageMatch } = useLocationMatch();
  const { mobile } = useResponsiveConstants();
  const { isStreaming, handleClear, snipNoteMutation, stopGenerating } =
    useChatPanelCommonProps({
      currentFeedOrDocumentId,
      searchWithin,
      pro,
      sessionState,
      report,
      lastActiveSessionId,
    });
  const StarGradient = getLogo('ask_needl_star_gradient');

  const hideNewChatButton = React.useMemo(() => {
    if (report.enabled) {
      return sessionState.messages.length > 0 ? false : true;
    } else {
      return isFeatureConfigLoading || featureConfig?.ask_needl_history;
    }
  }, [
    featureConfig?.ask_needl_history,
    isFeatureConfigLoading,
    report.enabled,
    sessionState.messages.length,
  ]);

  return (
    <div
      className={`flex flex-col items-center gap-12 h-full ${
        !report.enabled ? 'justify-center' : ''
      }`}
    >
      {report.enabled ? (
        <div className='w-full'>
          <NeedlReportPlaceholder report={report} />
        </div>
      ) : null}
      <div
        className={`w-full flex flex-col items-center ${
          report.enabled ? 'gap-4 mt-6' : 'gap-12'
        }`}
      >
        <div className='w-6 h-6 text-black'>
          <StarGradient />
        </div>
        {!mobile && (askNeedlPageMatch || report.enabled) ? (
          <Footer
            onSubmit={handleAskQuestion}
            handleClear={handleClear}
            isLoading={snipNoteMutation.isLoading}
            hasMessages={sessionState.messages.length > 0}
            isStreaming={isStreaming}
            stopGenerating={stopGenerating}
            rows={1}
            hideNewChatButton={hideNewChatButton}
          />
        ) : null}
      </div>

      {report.enabled && currentFeedOrDocumentId === report.info?.feed_id ? (
        <NeedlReportQuestions
          report={report}
          onQuestionSelect={handleAskQuestion}
          currentFeed={currentFeedOrDocumentId}
        />
      ) : (
        <DefaultQuestions
          searchWithin={searchWithin}
          currentFeedOrDocumentId={currentFeedOrDocumentId}
          onQuestionSelect={handleAskQuestion}
        />
      )}
    </div>
  );
};

type PlaceholderType = {
  handleAskQuestion: (message: string) => void;
  pro: boolean;
  sessionState: SessionState;
  report: AskNeedlReportType;
  lastActiveSessionId: string | null;
};
